/**
 * Fleet-wide intents: the overview, hot spots, bridging units, the risk
 * ranking, how far to trust a prediction, the breakdown trend and one
 * component's failure history.
 *
 * Ported branches keep their demo answers word for word (demo line refs
 * inline); the class / mine / component filters are the fork's.
 */

import { BY_ID, FLEET, HEALTH, IDS, MINES } from '../../../data/core.ts';
import { groupNum } from '../../oem-engine.ts';
import { inClass } from '../entities.ts';
import { route, scopeLabel } from '../answer-kit.ts';
import { BRIDGING } from '../bridging.ts';
import { TOOLS } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

/** `BREAK_DOWN_AS_VERB` — "break down cost by crew" is a cost question. See branch 17. */
export const BREAK_DOWN_AS_VERB = /break ?down (of |the )*(cost|spend|ledger)|break down .{0,24}\bby\b/;

// ---------------------------------------------------------------------------
// fleet_overview — demo L4081, the old catch-all, now asked for by name
// ---------------------------------------------------------------------------

export const fleetOverview: IntentDef = {
  id: 'fleet_overview',
  family: 'fleet',
  priority: 10,
  examples: ['Give me a fleet overview', 'How is the fleet right now?', "What's the status across the sites?"],
  score: cues({
    any: [
      /fleet (overview|summary|status|right now|today|health)/,
      /how is the fleet|how'?s the fleet|how are (we|things) (doing|looking|tracking)/,
      /what'?s the status|what is the status|status (update|across|of the (fleet|sites|mines))/,
      /overview of (the )?(fleet|sites|mines|everything)/,
      /\b(summary|overview) (of )?(all|every) (sites|mines)/,
    ],
  }),
  async answer({ kit }) {
    const o = await kit.run(TOOLS.fleet_overview, {});
    const markdown =
      `**Fleet right now** — ${FLEET.length} machines. ` +
      o.mines
        .map(
          (m) =>
            `**${m.name}** (${m.state}, ${m.depth_m} m, ${m.data_mode.split(' (')[0]}): ${m.machines} machines, ` +
            // demo bug: `flagged_critical` / `flagged_watch` were never emitted.
            `critical ${m.flagged_critical.join('/') || 'none'}, watch ${m.flagged_watch.length}, ` +
            `A$${m.avg_cost_per_op_hour}/op h`,
        )
        .join('. ') +
      `.\n\nOpen alerts: ${o.open_alerts.join('; ')}.\n\n` +
      `Ask me about a machine (e.g. ${IDS.L14}), a trend, a cost comparison, stock for a part, or what to change next.`;
    return kit.done(markdown, ['fleet_overview']);
  },
};

// ---------------------------------------------------------------------------
// hotspots / bridging_units — demo L4058
// ---------------------------------------------------------------------------

export const hotspots: IntentDef = {
  id: 'hotspots',
  family: 'fleet',
  priority: 92,
  examples: ['Which machines are hot spots right now?'],
  score: cues({ any: [/hot ?spots?/], not: [/bridging/] }),
  async answer({ kit, E }) {
    kit.ran('health_hotspots', { at_or_below: 40 });
    const hot = HEALTH.filter((h) => h.health_score <= 40 && inClass(h.machine_id, E.cls))
      .filter((h) => !E.mine || BY_ID[h.machine_id].mine === E.mine)
      .slice()
      .sort((a, b) => a.health_score - b.health_score);
    const markdown =
      `**${hot.length} hot spots** (components at 40/100 or below): ` +
      `${hot
        .map(
          (h) =>
            `**${h.machine_id}** ${h.component} ${h.health_score}/100 (${
              MINES[BY_ID[h.machine_id].mine].name.split(' ')[0]
            })`,
        )
        .join('; ')}. ` +
      `Each carries an agent-drafted work order in Approvals or an open alert.`;
    if (hot[0]) kit.focus({ machine: hot[0].machine_id, component: hot[0].component });
    return kit.done(markdown, ['component_health.csv', 'fleet_register.csv'], {
      navigate: route('population'),
    });
  },
};

export const bridgingUnits: IntentDef = {
  id: 'bridging_units',
  family: 'fleet',
  priority: 93,
  examples: ['List the bridging units'],
  score: cues({ any: [/bridging/] }),
  async answer({ kit }) {
    kit.ran('bridging_units');
    const markdown = `**${Object.keys(BRIDGING).length} bridging units** in the population: ${Object.entries(BRIDGING)
      .map(([k, v]) => `**${k}** — ${v}`)
      .join('; ')}.`;
    return kit.done(markdown, ['component_health.csv', 'fleet_register.csv'], {
      navigate: route('population'),
    });
  },
};

// ---------------------------------------------------------------------------
// risk_ranking — demo L4071, with class filters
// ---------------------------------------------------------------------------

export const riskRanking: IntentDef = {
  id: 'risk_ranking',
  family: 'fleet',
  priority: 70,
  examples: [
    'Which machines are most likely to fail next and why?',
    'Which surface drills are at risk this week?',
    'What is the failure risk at Mount Isolde?',
  ],
  score: cues({
    any: [
      /predict/,
      /\brisk(s|y)?\b|at risk/,
      /likely to (fail|break)|(fail|break)(s|ing)? next|next to (fail|break)|going to (fail|break)|will (fail|break)|about to (fail|break)/,
      /which (machines?|units?|rigs?) (should|do) (i|we) (worry|watch)|(worry|worried|concerned) about|keep an eye on/,
    ],
    not: [/\bcost|spend|\bprice|how confident|confidence/],
    penalty: [
      [/\b(e-?mail|report|deck|presentation|slides)\b/, 4],
      [/\bdid\b.*\bfail|why did|what caused/, 4],
    ],
  }),
  async answer({ kit, E }) {
    const cls = E.cls && !E.machine ? E.cls.classes.join(',') : null;
    const r = await kit.run(TOOLS.predict_risk, {
      machine_id: E.machine ?? null,
      mine: E.machine ? null : (E.mine ?? null),
      ...(cls ? { cls } : {}),
      ...(E.cls?.subclass && !E.machine ? { subclass: E.cls.subclass } : {}),
    });
    const scope =
      scopeLabel(E) + (E.cls && !E.machine ? ` · ${E.cls.label}` : '');
    const markdown =
      `**Failure risk ranking${scope}**\n\n` +
      `| Machine | Risk (0–1) | Drivers | Action |\n|---|---|---|---|\n` +
      r
        .slice(0, 6)
        .map(
          (x) =>
            `| ${x.machine} (${x.model}, ${x.mine}) | ${x.risk} | ${x.factors.join('; ') || '—'} | ${x.recommendation} |`,
        )
        .join('\n') +
      `\n\nRisk is a heuristic score 0–1 combining flagged component health, telemetry drift, breakdowns since June, ` +
      `late PMs and abuse events. Above 0.6 means plan the change-out this week; the pattern to watch is a metric ` +
      `drifting for a week or more on a component past its change interval.`;
    if (r[0]) kit.focus({ machine: r[0].machine, mine: r[0].mine });
    kit.remember({ ranked: r.slice(0, 6).map((x) => x.machine) });
    return kit.done(markdown, ['predict_risk', 'component_health', 'daily telemetry']);
  },
};

// ---------------------------------------------------------------------------
// prediction_confidence — new
// ---------------------------------------------------------------------------

export const predictionConfidence: IntentDef = {
  id: 'prediction_confidence',
  family: 'fleet',
  priority: 72,
  examples: ['How confident should I be in the J-04 prediction?', 'How sure is the model about T-09?'],
  needs: ['machine'],
  score: cues({
    any: [
      /how confident|confidence|how sure|how reliable|how accurate|how much (can|should) (i|we) trust|trust (the|this|that)/,
    ],
    base: 6,
  }),
  async answer({ kit, E }) {
    const id = E.machine as string;
    const a = await kit.run(TOOLS.alert_confidence, { machine_id: id });
    kit.focus({ machine: id, mine: BY_ID[id].mine });
    if (!a) {
      const r = await kit.run(TOOLS.predict_risk, { machine_id: id });
      const row = r[0];
      const markdown =
        `**${id}${scopeLabel(E).replace(` · ${id}`, '')}** has no open prediction — no alert has fired on it. ` +
        `Its heuristic risk score is **${row?.risk ?? 0}** (0–1)${row?.factors.length ? `, driven by ${row.factors.join('; ')}` : ''}: ` +
        `${row?.recommendation ?? 'No action'}.\n\n` +
        `A risk score is a ranking aid, not a model confidence — it has no failure window. ` +
        `Confidence only exists once the Diagnostic agent raises an alert with evidence behind it.`;
      return kit.done(markdown, ['predict_risk', 'component_health', 'daily telemetry']);
    }
    kit.focus({ component: a.component, alert: a.alert });
    const lean = a.confidence_pct >= 85 ? 'act on it' : a.confidence_pct >= 70 ? 'plan on it' : 'watch it';
    const markdown =
      `**${a.machine} · ${a.component}** — ${a.title} (${a.alert}, ${a.mode === 'LIVE' ? 'live feed' : 'shift-end read'}, raised ${a.raised}).\n\n` +
      `**${a.confidence_pct}% model confidence**, window **${a.window}** — enough to ${lean}.\n\n` +
      `What it rests on:\n${a.evidence.map((e) => `- ${e}`).join('\n')}\n\n` +
      (a.diagnostic ? `Diagnostic agent: ${a.diagnostic}\n\n` : '') +
      (a.planned != null && a.unplanned != null
        ? `Being wrong is cheap: the planned job is **A$${groupNum(a.planned)}** against **A$${groupNum(a.unplanned)}** if it fails first.`
        : `Reasoning: ${a.why.split('. ')[0]}.`);
    return kit.done(markdown, ['alerts', 'alert evidence', 'rate card'], {
      navigate: route('alerts', a.alert),
    });
  },
};

// ---------------------------------------------------------------------------
// breakdown_trend — demo L4073, with class and component filters
// ---------------------------------------------------------------------------

export const breakdownTrend: IntentDef = {
  id: 'breakdown_trend',
  family: 'fleet',
  priority: 66,
  examples: ['What is the breakdown trend across the six mines?', 'Which loaders break down most?'],
  score: cues({
    any: [
      /trend/,
      /breaking/,
      /break down/,
      /failure rate/,
      /\bcomponents?\b/,
      /fail(s|ing|ures?)? (the )?most|most (common )?(failures?|fail)/,
      /keep (breaking|failing)/,
    ],
    not: [BREAK_DOWN_AS_VERB, /\bcost|spend|expensive|per hour/],
    penalty: [[/change next|replace next|should (we|i) (change|replace)/, 4]],
  }),
  async answer({ kit, E }) {
    const cls = E.cls ? E.cls.classes.join(',') : undefined;
    const t = await kit.run(TOOLS.breakdown_trend, {
      mine: E.mine ?? null,
      machine_id: E.machine ?? null,
      cls,
    });
    const m = t.by_month;
    const dir =
      t.trend_breakdowns_per_month_slope > 0.05
        ? 'rising'
        : t.trend_breakdowns_per_month_slope < -0.05
          ? 'falling'
          : 'flat';
    const markdown =
      `**Breakdown trend${E.mine ? ` · ${MINES[E.mine].name}` : ''}${E.cls ? ` · ${E.cls.label}` : ''}** — ${t.events} reactive work orders over ` +
      `${m.length} months; breakdowns per month are **${dir}** (slope ${t.trend_breakdowns_per_month_slope}/month; ` +
      `last 3 months ${t.last3_vs_first3_breakdowns[0]} vs first 3 months ${t.last3_vs_first3_breakdowns[1]}).\n\n` +
      `| Mine | Reactive WOs | Breakdowns | Mean down h | PMs late | Operator abuse |\n|---|---|---|---|---|---|\n` +
      t.by_mine
        .map(
          (x) =>
            `| ${x.name} | ${x.events} | ${x.breakdowns} | ${x.mean_downtime_h} | ${Math.round(
              x.pm_late_share * 100,
            )}% | ${x.operator_abuse_events} |`,
        )
        .join('\n') +
      `\n\nTop components: ${t.top_components.slice(0, 5).join(', ')}. Top causes: ${t.top_causes
        .slice(0, 4)
        .join(', ')}.\n\n` +
      `To get ahead of it: the coupling failures (${t.coupling_history.join('; ')}) show the precursor is a ` +
      `two-week hydraulic-temperature drift plus an overdue oil sample — detectable in shift-end data. ` +
      `That is the predictive rule TB-2026-031 now encodes.`;
    return kit.done(markdown, ['breakdown_trend', 'SAP PM work_orders', 'TB-2026-031']);
  },
};

// ---------------------------------------------------------------------------
// component_failure_history — new: "how many coupling failures and where?"
// ---------------------------------------------------------------------------

export const componentFailureHistory: IntentDef = {
  id: 'component_failure_history',
  family: 'fleet',
  priority: 74,
  examples: ['How many coupling failures have we had and where?', 'Where have the turbo failures been?'],
  score: cues({
    any: [
      /how many .*(failures?|breakdowns?|failed|shear)/,
      /(failures?|breakdowns?) (have we had|so far|to date|history)/,
      /where (have|did) .*(fail|break)/,
      /history of .*(failures?|breakdowns?)/,
    ],
    when: (E) => E.components.length > 0,
    base: 5,
  }),
  async answer({ kit, E }) {
    const c = E.components[0];
    const t = await kit.run(TOOLS.breakdown_trend, {
      mine: E.mine ?? null,
      cls: E.cls ? E.cls.classes.join(',') : null,
      component: c.stock === 'dth' ? 'hammer' : c.word,
    });
    kit.ran('work_orders', { type: 'Breakdown', component: c.word });
    const byMine = t.by_mine.filter((x) => x.breakdowns > 0);
    const where = byMine.map((x) => `${x.name} ${x.breakdowns}`).join(', ') || 'none';
    const history = c.stock === 'coupling' ? t.coupling_history : [];
    const markdown =
      `**${t.by_mine.reduce((a, x) => a + x.breakdowns, 0)} ${c.word} breakdowns** in 18 months ` +
      `(${t.events} reactive work orders mention it, breakdowns and correctives together)${scopeLabel(E, { machine: false })}.\n\n` +
      `By mine: ${where}.\n\n` +
      (history.length
        ? `The three pump-drive coupling failures, in order: ${history.join('; ')}. ` +
          `One was end-of-life; the two Kalbarri shears were early, both on contaminated oil with samples overdue — ` +
          `the precursor TB-2026-031 now encodes (hydraulic temperature drift plus an overdue sample).`
        : `Top causes: ${t.top_causes.slice(0, 4).join(', ') || '—'}. Top components: ${t.top_components
            .slice(0, 3)
            .join(', ')}.`);
    kit.remember({ component: c.word });
    return kit.done(markdown, ['breakdown_trend', 'SAP PM work_orders', ...(history.length ? ['TB-2026-031'] : [])]);
  },
};
